import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const TurtleNestingTable = () => {
  const nestingData = [
    { year: '2014', incidents: 13 },
    { year: '2015', incidents: 25 },
    { year: '2016', incidents: 18 },
    { year: '2017', incidents: 32 },
    { year: '2018', incidents: 27 },
    { year: '2019', incidents: 43 },
    { year: '2020', incidents: 38 },
    { year: '2021', incidents: 29 },
    { year: '2022', incidents: 35 },
    { year: '2023', incidents: 41 },
    { year: '2024', incidents: 37 }
  ];

  const totalIncidents = nestingData?.reduce((sum, item) => sum + item?.incidents, 0);
  const averageIncidents = (totalIncidents / nestingData?.length)?.toFixed(1);

  return (
    <div className="bg-card rounded-lg shadow-conservation p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="Activity" size={24} className="text-secondary" />
            Marine Turtle Nesting Activities & Incidents (2014-2024)
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Total recorded incidents: {totalIncidents} | Average: {averageIncidents} per year
          </p>
        </div>
        <div className="bg-secondary/10 px-4 py-2 rounded-lg">
          <p className="text-xs text-muted-foreground">2024 Incidents</p>
          <p className="text-2xl font-bold text-secondary">37</p>
        </div>
      </div>
      {/* Line Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={nestingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis 
            dataKey="year" 
            stroke="var(--color-muted-foreground)"
            tick={{ fill: 'var(--color-muted-foreground)' }}
          />
          <YAxis 
            stroke="var(--color-muted-foreground)"
            tick={{ fill: 'var(--color-muted-foreground)' }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px'
            }}
            labelStyle={{ color: 'var(--color-foreground)' }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="incidents" 
            name="Nesting Incidents"
            stroke="var(--color-secondary)" 
            strokeWidth={3}
            dot={{ fill: 'var(--color-secondary)', r: 5 }}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {/* Data Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Year</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Nesting Activities/Incidents</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">vs. Previous Year</th>
            </tr>
          </thead>
          <tbody>
            {nestingData?.map((item, index) => {
              const prevYear = index > 0 ? nestingData?.[index - 1]?.incidents : null;
              const difference = prevYear ? item?.incidents - prevYear : null;
              const isIncrease = difference > 0;
              
              return (
                <tr key={item?.year} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm text-foreground font-medium">{item?.year}</td>
                  <td className="py-3 px-4 text-sm text-foreground text-right font-medium">
                    {item?.incidents}
                  </td>
                  <td className="py-3 px-4 text-sm text-right">
                    {difference !== null ? (
                      <span className={`inline-flex items-center gap-1 ${isIncrease ? 'text-success' : 'text-error'}`}>
                        <Icon name={isIncrease ? 'ArrowUp' : 'ArrowDown'} size={14} />
                        {Math.abs(difference)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Activity Breakdown */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm font-medium text-foreground">Successful Nesting</p>
          </div>
          <p className="text-2xl font-bold text-success">85%</p>
          <p className="text-xs text-muted-foreground mt-1">Of recorded incidents</p>
        </div>
        
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <p className="text-sm font-medium text-foreground">Threatened Nests</p>
          </div>
          <p className="text-2xl font-bold text-warning">12%</p>
          <p className="text-xs text-muted-foreground mt-1">Requiring intervention</p>
        </div>
        
        <div className="bg-info/10 border border-info/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="MapPin" size={20} className="text-info" />
            <p className="text-sm font-medium text-foreground">Protected Sites</p>
          </div>
          <p className="text-2xl font-bold text-info">8</p>
          <p className="text-xs text-muted-foreground mt-1">Active monitoring zones</p>
        </div>
      </div>
      {/* Conservation Note */}
      <div className="mt-4 bg-secondary/10 border border-secondary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-secondary mt-0.5" />
          <div>
            <p className="text-sm text-foreground font-medium">Nesting Activity Monitoring</p>
            <p className="text-xs text-muted-foreground mt-1">
              Nesting activities include egg laying events, nest site preparation, false crawls, 
              and documented turtle beach visits. Fluctuations reflect seasonal patterns, 
              environmental conditions, and the effectiveness of beach protection measures.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurtleNestingTable;